import type { ListMedicalCodesByEncounterResponse } from "@elation/api-client/src/stitch-web/models/listMedicalCodesByEncounterResponse";
import type { MedicalCodeRow } from "@elation/api-client/src/stitch-web/models/medicalCodeRow";
import { MedicalCodeRowCreationType } from "@elation/api-client/src/stitch-web/models/medicalCodeRowCreationType";

export type MedicalCodeGroup = "icd10" | "cpt" | "vbc";

export const MEDICAL_CODE_GROUPS = [
  "icd10",
  "cpt",
  "vbc",
] as const satisfies readonly MedicalCodeGroup[];

export const MEDICAL_CODES_POLL_INTERVAL_MS = 3_000;

export type MedicalCodeSelection = {
  code: string;
  group: MedicalCodeGroup;
};

type MaybeMedicalCodeRow = MedicalCodeRow | null | undefined;
type MaybeMedicalCodesResponse =
  | Pick<ListMedicalCodesByEncounterResponse, "codingStatus">
  | null
  | undefined;

const GROUP_LABELS: Record<MedicalCodeGroup, string> = {
  icd10: "ICD-10",
  cpt: "CPT",
  vbc: "VBC",
};

const GROUP_SECTION_TITLES: Record<MedicalCodeGroup, string> = {
  icd10: "Diagnosis codes (ICD-10)",
  cpt: "Procedure codes (CPT)",
  vbc: "VBC codes",
};

const CREATION_TYPE_LABELS: Record<
  NonNullable<MedicalCodeRow["creationType"]>,
  string
> = {
  [MedicalCodeRowCreationType.ai]: "AI suggested",
  [MedicalCodeRowCreationType.template]: "Template",
};

function normalizedCode(
  row: Pick<MedicalCodeRow, "code"> | null | undefined,
): string {
  return row?.code.trim().toUpperCase() ?? "";
}

export const MedicalCodeEntity = {
  isICD10(group: MedicalCodeGroup | null | undefined): group is "icd10" {
    return group === "icd10";
  },

  isCPT(group: MedicalCodeGroup | null | undefined): group is "cpt" {
    return group === "cpt";
  },

  isVBC(group: MedicalCodeGroup | null | undefined): group is "vbc" {
    return group === "vbc";
  },

  isCodingPending(response: MaybeMedicalCodesResponse): boolean {
    return response?.codingStatus === "pending";
  },

  isCodingReady(response: MaybeMedicalCodesResponse): boolean {
    return response?.codingStatus === "ready";
  },

  isCodingFailed(response: MaybeMedicalCodesResponse): boolean {
    return response?.codingStatus === "failed";
  },

  isCodingEmpty(response: MaybeMedicalCodesResponse): boolean {
    return response?.codingStatus === "none";
  },

  medicalCodesPollIntervalMs(
    response: MaybeMedicalCodesResponse,
  ): typeof MEDICAL_CODES_POLL_INTERVAL_MS | false {
    return MedicalCodeEntity.isCodingPending(response)
      ? MEDICAL_CODES_POLL_INTERVAL_MS
      : false;
  },

  isValid(row: Pick<MedicalCodeRow, "code"> | null | undefined): boolean {
    return normalizedCode(row).length > 0;
  },

  modifiers(
    row:
      | Pick<
          MedicalCodeRow,
          "modifier1" | "modifier2" | "modifier3" | "modifier4"
        >
      | null
      | undefined,
  ): string[] {
    return [row?.modifier1, row?.modifier2, row?.modifier3, row?.modifier4]
      .map((modifier) => modifier?.trim())
      .filter((modifier): modifier is string => !!modifier);
  },

  codeLabel(row: MaybeMedicalCodeRow): string | null {
    if (!row) return null;
    const code = normalizedCode(row);
    const modifiers = MedicalCodeEntity.modifiers(row);
    const units =
      typeof row?.units === "number" && row.units > 1 ? ` x${row.units}` : "";

    return `${code}${modifiers.length ? `-${modifiers.join("-")}` : ""}${units}`;
  },

  groupLabel(group: MedicalCodeGroup): string {
    return GROUP_LABELS[group];
  },

  groupSectionTitle(group: MedicalCodeGroup): string {
    return GROUP_SECTION_TITLES[group];
  },

  descriptionLabel(
    row: { description?: string | null } | null | undefined,
    fallback = "Suggested code",
  ): string {
    return row?.description?.trim() || fallback;
  },

  creationTypeLabel(
    row: Pick<MedicalCodeRow, "creationType"> | null | undefined,
  ): string | null {
    if (!row) return null;
    return row.creationType ? CREATION_TYPE_LABELS[row.creationType] : null;
  },

  isAiSuggested(
    row: Pick<MedicalCodeRow, "creationType"> | null | undefined,
  ): boolean {
    if (!row) return false;
    return row.creationType === MedicalCodeRowCreationType.ai;
  },

  isTemplateSuggested(
    row: Pick<MedicalCodeRow, "creationType"> | null | undefined,
  ): boolean {
    if (!row) return false;
    return row.creationType === MedicalCodeRowCreationType.template;
  },

  evidenceDisplayLines(
    row: Pick<MedicalCodeRow, "evidence"> | null | undefined,
  ): string[] {
    return (
      row?.evidence.flatMap((item) =>
        item.quotes.length > 0 ? item.quotes : [item.narrative],
      ) ?? []
    ).filter((line) => line.trim().length > 0);
  },

  hasEvidence(
    row: Pick<MedicalCodeRow, "evidence"> | null | undefined,
  ): boolean {
    return MedicalCodeEntity.evidenceDisplayLines(row).length > 0;
  },

  isSelected(
    row: Pick<MedicalCodeRow, "code"> | null | undefined,
    group: MedicalCodeGroup,
    selection: MedicalCodeSelection | readonly MedicalCodeSelection[] | null,
  ): boolean {
    if (!selection) return false;

    const selections = Array.isArray(selection) ? selection : [selection];
    const code = normalizedCode(row);

    return selections.some(
      (selected) =>
        selected.group === group && selected.code.trim().toUpperCase() === code,
    );
  },

  groupRows(
    response:
      | Pick<ListMedicalCodesByEncounterResponse, "icd10" | "cpt" | "vbc">
      | null
      | undefined,
  ): Record<MedicalCodeGroup, MedicalCodeRow[]> {
    return {
      icd10: response?.icd10 ?? [],
      cpt: response?.cpt ?? [],
      vbc: response?.vbc ?? [],
    };
  },

  compareByCodeAsc(left: MedicalCodeRow, right: MedicalCodeRow): number {
    return normalizedCode(left).localeCompare(normalizedCode(right));
  },
} as const;
